import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ScanLine, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_APP_URL;
const MONTEUR_LOGIN_URL = `${PUBLIC_APP_URL}/monteur/login`;

/**
 * Druckbares Mitarbeiterzugangs-Blatt (P1, Juni 2026).
 *
 * Rein additiv: zeigt einen allgemeinen QR-Code auf die BESTEHENDE
 * Mitarbeiter-Login-Seite (`/monteur/login`). KEIN Auto-Login, KEIN Token,
 * KEINE Auth-/Backend-Berührung. Der Mitarbeiter scannt und meldet sich wie
 * gewohnt mit Firmen-Code + PIN an.
 */
export function MitarbeiterZugangPrint({ open, onOpenChange, companyName, companyCode }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="zugang-print-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> Mitarbeiterzugang drucken
          </DialogTitle>
          <DialogDescription>
            Ausdrucken und im Fahrzeug, Aufenthaltsraum oder in der Werkstatt
            aufhängen. Mitarbeiter scannen den QR-Code und melden sich mit ihrer
            PIN an.
          </DialogDescription>
        </DialogHeader>

        {/* Vorschau = exakt das Druck-Blatt */}
        <div className="zugang-print-area rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center">
          <img
            src="/brand/clartext-logo-full-light.svg"
            alt="CLartext"
            className="h-12 w-auto mx-auto mb-8 object-contain"
            data-testid="zugang-print-logo"
          />

          <p className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-1">
            Mitarbeiter-Anmeldung
          </p>
          <h2
            className="font-heading text-2xl font-bold text-slate-900 mb-8"
            data-testid="zugang-print-company-name"
          >
            {companyName || "—"}
          </h2>

          <div className="flex justify-center mb-6">
            <div className="rounded-2xl border-4 border-slate-900 p-4 bg-white">
              <QRCodeSVG
                value={MONTEUR_LOGIN_URL}
                size={208}
                level="M"
                marginSize={0}
                data-testid="zugang-print-qr"
              />
            </div>
          </div>

          <ol className="text-left max-w-sm mx-auto text-sm text-slate-700 space-y-2 mb-8">
            <li className="flex gap-2">
              <span className="font-heading font-bold text-slate-900">1.</span>
              QR-Code mit der Handy-Kamera scannen.
            </li>
            <li className="flex gap-2">
              <span className="font-heading font-bold text-slate-900">2.</span>
              Firmen-Code eingeben:
              <strong
                className="font-heading tracking-wide text-slate-900"
                data-testid="zugang-print-company-code"
              >
                {companyCode || "—"}
              </strong>
            </li>
            <li className="flex gap-2">
              <span className="font-heading font-bold text-slate-900">3.</span>
              Mit der persönlichen 4-stelligen PIN anmelden.
            </li>
          </ol>

          <div className="text-[11px] text-slate-400 break-all" data-testid="zugang-print-url">
            {MONTEUR_LOGIN_URL}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="zugang-print-close"
          >
            <X className="w-4 h-4 mr-1.5" />
            Schließen
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="zugang-print-button"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            Drucken
          </Button>
        </DialogFooter>

        {/* Druck-Styles: nur das Blatt drucken, alles andere ausblenden. */}
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            .zugang-print-area, .zugang-print-area * { visibility: visible !important; }
            .zugang-print-area {
              position: fixed !important;
              inset: 0 !important;
              margin: 0 !important;
              border: none !important;
              border-radius: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: center !important;
              padding: 24mm 18mm !important;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

export default MitarbeiterZugangPrint;
